import React from 'react';
import { ChevronDown, ChevronUp, Copy, Trash2 } from 'lucide-react';
import { FrequencySettingsFields } from './FrequencySettingsFields';
import type { Frequency } from '../PackSizesPanel';
interface FrequencyRowProps {
  freq: Frequency;
  customTiers: string[];
  onAddCustomTier: (tier: string) => void;
  onToggle: () => void;
  onUpdate: (updates: Partial<Frequency>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}
const INTERVAL_OPTIONS: Record<string, number[]> = {
  Month: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  Week: [1, 2, 3, 4],
  Day: [7, 14, 21, 28, 30, 60, 90]
};
export function FrequencyRow({
  freq,
  customTiers,
  onAddCustomTier,
  onToggle,
  onUpdate,
  onDelete,
  onDuplicate
}: FrequencyRowProps) {
  const isCollapsed = freq.isRowExpanded === false;
  return <div className={isCollapsed ? 'bg-white border-l-[3px] border-l-[#10B981]' : 'bg-[#F0F9FF] border-l-[3px] border-l-[#0EA5E9]'} data-id="element-3950" data-mp="k7jw8pr0v0pq">
      {isCollapsed ? <div className="flex items-center justify-between px-4 py-3 hover:bg-[#F8FAFC] transition-colors cursor-pointer" onClick={onToggle} data-id="element-3951" data-mp="p3qsfqtm8xuh">
          <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-3952" data-mp="1fceky513p2ff">
            <div className="w-2 h-2 rounded-full bg-[#10B981] flex-shrink-0" data-id="element-3953" data-mp="1ffaivtcy7r3v" />
            <span className="text-[14px] font-semibold text-[#0F172A] flex-shrink-0" data-id="element-3954" data-mp="1akek8ad7i5hs">
              {freq.interval || freq.name || 'New Frequency'}
            </span>
            {freq.discount && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold bg-[#D1FAE5] text-[#059669] flex-shrink-0" data-id="element-3955" data-mp="11n4aitg1eoez">
                {freq.discount}
              </span>}
            <span className="text-[13px] text-[#94A3B8] font-mono truncate hidden sm:block" data-id="element-3956" data-mp="rnlu2mzc5kro">
              sp_
              {freq.name ? freq.name.toLowerCase() : 'plan'}_
              {freq.interval ? freq.interval.replace(/\s+/g, '_').toLowerCase() : 'interval'}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3957" data-mp="2h333gxh32dun">
            <button className="p-1.5 text-[#94A3B8] hover:text-[#64748B] hover:bg-[#F1F5F9] rounded transition-colors" aria-label="Duplicate frequency" onClick={e => {
          e.stopPropagation();
          onDuplicate();
        }} data-id="element-3958" data-mp="fc2ynosu565m">
              <Copy className="w-4 h-4" data-id="element-3959" data-mp="5grf8t6hfarj" />
            </button>
            <button className="p-1.5 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" aria-label="Delete frequency" onClick={e => {
          e.stopPropagation();
          onDelete();
        }} data-id="element-3960" data-mp="af2rtbo5vzdg">
              <Trash2 className="w-4 h-4" data-id="element-3961" data-mp="131x9y1xiamnr" />
            </button>
            <ChevronDown className="w-4 h-4 text-[#94A3B8] ml-1" data-id="element-3962" data-mp="p3p93ixcn17b" />
          </div>
        </div> : <>
          <div className="flex items-center justify-between px-4 py-2 bg-[#E0F2FE] border-b border-[#BAE6FD]" data-id="element-3963" data-mp="t3hhnvmqt7co">
            <div className="flex items-center gap-2" data-id="element-3964" data-mp="39sd9dhhtvidj">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-[#0EA5E9] text-white uppercase tracking-wide" data-id="element-3965" data-mp="3duhz4555nuuw">
                Editing
              </span>
              {freq.name || freq.interval ? <span className="text-[13px] font-semibold text-[#0369A1]" data-id="element-3966" data-mp="31b46wf89qpcg">
                  {freq.interval || freq.name}
                </span> : <span className="text-[13px] text-[#0EA5E9]" data-id="element-3967" data-mp="3g8n5e9ar0gx3">
                  New frequency — select options below
                </span>}
            </div>
            <button className="flex items-center gap-1 text-[12px] font-medium text-[#0369A1] hover:text-[#0F172A] hover:bg-[#BAE6FD] px-2 py-1 rounded transition-colors" onClick={onToggle} data-id="element-3968" data-mp="2njy161eebvh4">
              <ChevronUp className="w-3.5 h-3.5" data-id="element-3969" data-mp="2xb2g5142ngby" />
              Collapse
            </button>
          </div>
          <div className="grid grid-cols-12 gap-4 px-4 py-4 items-center bg-white" data-id="element-3970" data-mp="2sjkd87ihn5oc">
            <div className="col-span-3" data-id="element-3971" data-mp="24tzrjguy9ubd">
              <div className="relative" data-id="element-3972" data-mp="24soq3noz8ynf">
                <select value={freq.name} onChange={e => onUpdate({
              name: e.target.value,
              interval: ''
            })} aria-label="Frequency" className="w-full h-[36px] px-3 pr-8 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-3973" data-mp="1zv6e1ofny3b7">
                  <option value="" disabled data-id="element-3974" data-mp="1gv6eyyb4y26t">
                    Select...
                  </option>
                  <option value="Day" data-id="element-3975" data-mp="1lu9jmhn7e6e9">Day</option>
                  <option value="Week" data-id="element-3976" data-mp="1lthqddh2wkr5">Week</option>
                  <option value="Month" data-id="element-3977" data-mp="1qt40vf5ivmw9">Month</option>
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-3978" data-mp="1qv70mqwh8ue6" />
              </div>
            </div>
            <div className="col-span-3" data-id="element-3979" data-mp="1ztinchu8381c">
              <div className="relative" data-id="element-3980" data-mp="gy440zq9hb0">
                <select value={freq.interval} disabled={!freq.name} onChange={e => onUpdate({
              interval: e.target.value
            })} aria-label="Interval" className="w-full h-[36px] px-3 pr-8 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#475569] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer disabled:bg-[#F1F5F9] disabled:text-[#94A3B8] disabled:cursor-not-allowed" data-id="element-3981" data-mp="hzqh1m71q81">
                  <option value="" disabled data-id="element-3982" data-mp="3fdi94z6m0g1p">
                    {freq.name ? 'Select interval...' : 'Select frequency first'}
                  </option>
                  {freq.name && (INTERVAL_OPTIONS[freq.name] ?? []).map(n => {
                const label = getIntervalLabel(freq.name, n);
                return <option key={n} value={label} data-id="element-3983" data-mp="6wnf897si5zr">
                        {label}
                      </option>;
              })}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] pointer-events-none" data-id="element-3984" data-mp="2vbjrn28iqjn" />
              </div>
            </div>
            <div className="col-span-2" data-id="element-3985" data-mp="2nkrcinl0kpbx">
              {freq.discount ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-[#D1FAE5] text-[#059669]" data-id="element-3986" data-mp="2p2zlykhav6f9">
                  {freq.discount}
                </span> : <input type="text" placeholder="e.g. 10" defaultValue={freq.discount} aria-label="Discount" className="w-full h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-3987" data-mp="2tzq4degkue3u" />}
            </div>
            <div className="col-span-3 flex items-center" data-id="element-3988" data-mp="2b3geltwbbz40">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[#F1F5F9] border border-[#E2E8F0] text-[13px] font-medium text-[#475569]" data-id="element-3989" data-mp="2b51elz1eca99">
                <span className="text-[15px] font-semibold text-[#0F172A]" data-id="element-3990" data-mp="21dwwiqgfc9a7">%</span>
                Percentage
              </span>
            </div>
            <div className="col-span-1 flex items-center justify-end" data-id="element-3991" data-mp="1rezc9zq171x0">
              <button className="p-1.5 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" aria-label="Delete frequency" onClick={e => {
            e.stopPropagation();
            onDelete();
          }} data-id="element-3992" data-mp="1rhbqc1so4s4s">
                <Trash2 className="w-4 h-4" data-id="element-3993" data-mp="1hs4tj8lubxxy" />
              </button>
            </div>
          </div>
          <div className="border-t border-[#E2E8F0] bg-[#FAFAFA]" data-id="element-3994" data-mp="1cs0lm6z00bjc">
            <div className="px-4 pt-4" data-id="element-3995" data-mp="1cuv8041ko6fe">
              <FrequencySettingsFields cssHandle={freq.name ? freq.name.toLowerCase().replace(/\s+/g, '-') : 'monthly'} storefrontHeading="Storefront Card Content For This Frequency" loyaltyTier={freq.loyaltyTier} adHocDiscount={freq.adHocDiscount} pausedDiscount={freq.pausedDiscount} customTiers={customTiers} onAddCustomTier={onAddCustomTier} onChange={onUpdate} data-id="element-3996" data-mp="1rozpbjqqbk8j" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 mt-2 border-t border-[#E2E8F0]" data-id="element-3997" data-mp="2afr7nasp2kz2">
              <button className="flex items-center gap-1.5 text-[13px] font-medium text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEF2F2] px-2 py-1.5 rounded-md transition-colors" onClick={onDelete} data-id="element-3998" data-mp="25hpmmkbpkbky">
                <Trash2 className="w-3.5 h-3.5" data-id="element-3999" data-mp="3gugw4c24ctkk" />
                Delete Frequency
              </button>
              <button className="flex items-center gap-1.5 text-[13px] font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] px-2 py-1.5 rounded-md transition-colors" onClick={onDuplicate} data-id="element-4000" data-mp="3lu1w8pt0z7yv">
                <Copy className="w-3.5 h-3.5" data-id="element-4001" data-mp="2t6p8icb4ou3z" />
                Duplicate
              </button>
            </div>
          </div>
        </>}
    </div>;
}
function getIntervalLabel(unit: string, n: number): string {
  const lower = unit.toLowerCase();
  const plural = n === 1 ? lower : `${lower}s`;
  return `Every ${n} ${plural}`;
}